import React, {cloneElement, useCallback, useEffect, useMemo, useRef, useState} from "react";
import {routeFunctions, routes} from "../../../../../../core/models/static/routes/app-routes";
import Button from "../../../../../components/base/button/button";
import useIsMounted from "../../../../../hooks/use-is-mounted";
import Api from "../../../../../../core/services/api/api";
import {Tab, Tabs} from "../../../../../components/base/Tabs";
import {Link, Route, Switch, useHistory, useParams} from "react-router-dom";
import ProductInformationBasicInformationView from "./basic";
import {Col} from "reactstrap";
import ProductInformationPricesView from "./prices";
import ProductInformationAvailabilityView from "./availability";
import ProductInformationImagesView from "./images";
import ProductInformationFeedbacksView from "./feedbacks";
import ProductInformationTechnicalFilesView from "./technical-files";
import ProductInformationNotesView from "./notes";
import ProductInformationRelationsView from "./relations";
import LoadingLottieAnimation from "../../../../../components/app-specific/lottie-animations/loading";
import BizPrintTemplatesDialog from "../../../../../components/dialogs/biz-print-templates";
import {ReactComponent as Printer} from '../../../../../../assets/images/printer.svg'
import IconButton from "../../../../../components/base/button/icon_button";
import BizPrintService from "../../../../../../core/services/biz-print/biz-print-service";
import {Alert, Box, CircularProgress, Skeleton, SvgIcon, Tooltip, Typography} from "@mui/material";
import {useConfigureNotebook} from "../../../../../hooks/use-notebook-config";
import ProductTechnicalPropertiesSection from "./technical-properties";
import UIUtils from "../../../../../utils";
import axios from "axios";
import ProductInformationPropertyGroupView from "./property-group";
import ProductInformationCodesView from "./codes";
import classnames from "classnames";
import {ReactComponent as SaveIcon} from "../../../../../../assets/images/save-rollback/check.svg";
import {CloudSyncOutlined, CopyAllRounded, DeleteOutlined, EditOutlined, FiberNewRounded, SettingsOutlined, Visibility, WarningOutlined} from "@mui/icons-material";
import {useCachedPropertyGroupsController} from "../../../../../hooks/use-industry";
import {MarkProductForCacheSectionNames} from "../../../../../../core/constants/enums";
import useAccessLevels from "../../../../../hooks/use-access-levels";
import InventoryAdjustmentRecordView from "../../inventory/inventory-adjustment/inventory-adjustment-record";
import InventoryAdjustmentReportView from "../../inventory/inventory-adjustment/inventory-adjustment-report";
import {Popover} from "../../../../../components/app-specific/popover";
import CopyProductDialog, {CopyProductDialogModes} from "../../../../../components/dialogs/copy-product-dialog";
import useBehavioralAnalyticsTool from "../../../../../hooks/use-behavioural-analytics-tool";
import ProductInformationStatsView from "./stats";
import ProductVarianceSection from "./variance";
import ProductInformationDimensionsView from "./dimensions";
import ProductInformationVendorsView from "./vendors";
import EditProductPartNumberDialog from "../../../../../components/dialogs/edit-product-part-number-dialog";
import ProductInformationPopover from "../../../../../components/app-specific/draggable-popover/product-information-popover";
import {useTranslation} from "react-i18next";
import CopyIconButton from "../../../../../components/base/copy-to-clipboard/copy-icon-button";

const initialBizPrintDialogState = {
    open: false,
    pageName: null,
    pageData: null,
}

const initialCopyProductDialogState = {
    open: false,
    mode: null,
    close: () => void 0,
    providedProduct: null,
}

const initialEditPartNumberDialogState = {
    open: false,
    onSuccess: () => void 0,
    close: () => void 0,
    partId: null,
    partNo: null,
}

export const ProductInformationTabs = {
    basic: 'basic',
    stats: 'stats',
    price: 'price',
    vendors: 'vendors',
    availability: 'availability',
    inventoryAdjustmentReport: 'inventory-adjustment-report',
    inventoryAdjustmentRecord: 'inventory-adjustment-record',
    technical: 'technical',
    variance: 'variance',
    dimensions: 'dimensions',
    images: 'images',
    codes: 'codes',
    feedback: 'feedback',
    technicalFiles: 'technical-files',
    notes: 'notes',
    relation: 'relation',
}

const ProductInformationView = () => {
    const params = useParams();
    const history = useHistory();
    const [loading, setLoading] = useState(true);
    const [loadingPropertyGroups, setLoadingPropertyGroups] = useState(true);
    const [markingForCache, setMarkingForCache] = useState(false);
    const [removing, setRemoving] = useState(false);
    const [propertyGroups, setPropertyGroups] = useState([]);
    const [product, setProduct] = useState({});
    const [bizPrintDialogState, setBizPrintDialogState] = useState(initialBizPrintDialogState);
    const [copyProductDialogState, setCopyProductDialogState] = useState(initialCopyProductDialogState);
    const [editPartNumberDialogState, setEditPartNumberDialogState] = useState(initialEditPartNumberDialogState);
    const isMounted = useIsMounted();
    const previousId = useRef(null);
    const {configureNotebookTags} = useConfigureNotebook('product-information-view');
    const {configureBehavioralTool} = useBehavioralAnalyticsTool();
    const {getPropertyGroupsForIndustry} = useCachedPropertyGroupsController()
    const operatorAccessLevels = useAccessLevels();
    const {t} = useTranslation('views', {keyPrefix: 'app-specific.product-information'});

    /**
     * As soon as the component is mounted:
     * - Configures the notebook tags for the view
     * - Configures the behavioral analytics tool for the view
     */
    useEffect(() => {
        configureNotebookTags((noteTags) => noteTags.productInformation(params.id, params.tab));
        configureBehavioralTool((tags) => tags.productInformation(params.id, params.tab));
    }, [configureBehavioralTool, configureNotebookTags, params.id, params.tab]);

    const tab = useMemo(() => params?.tab ?? ProductInformationTabs.basic, [params.tab]);

    const tabs = useMemo(() => {
        let tabs = Object.values(ProductInformationTabs).map(e => ({
            value: e,
            name: (function () {
                switch (e) {
                    case ProductInformationTabs.basic:
                        return 'Basic Information';
                    case ProductInformationTabs.stats:
                        return 'Statistics';
                    case ProductInformationTabs.price:
                        return 'Price';
                    case ProductInformationTabs.availability:
                        return 'Availability & Warehouses';
                    case ProductInformationTabs.dimensions:
                        return 'Dimensions';
                    case ProductInformationTabs.inventoryAdjustmentReport:
                        return 'Inventory Adjustment Report';
                    case ProductInformationTabs.inventoryAdjustmentRecord:
                        return 'Inventory Adjustment Record';
                    case ProductInformationTabs.technical:
                        return 'Technical Information';
                    case ProductInformationTabs.variance:
                        return 'Options';
                    case ProductInformationTabs.images:
                        return 'Images';
                    case ProductInformationTabs.codes:
                        return 'Codes';
                    case ProductInformationTabs.feedback:
                        return 'Feedback';
                    case ProductInformationTabs.technicalFiles:
                        return 'Technical Files';
                    case ProductInformationTabs.notes:
                        return 'Notes';
                    case ProductInformationTabs.relation:
                        return 'Relation';
                    case ProductInformationTabs.vendors:
                        return 'Vendors';
                    default:
                        return "";
                }
            })()
        }));
        tabs.splice(7, 0, ...propertyGroups.map(e => ({name: e.title, value: `property-group-${e.id}`,})));

        if (!operatorAccessLevels.packages.lookup.editProductBasicInformation) {
            tabs.splice(tabs.findIndex(e => e.value === ProductInformationTabs.basic), 1);
        }
        if (!operatorAccessLevels.packages.lookup.editProductPricing) {
            tabs.splice(tabs.findIndex(e => e.value === ProductInformationTabs.price), 1);
        }
        if (!operatorAccessLevels.packages.lookup.editProductInventory) {
            tabs.splice(tabs.findIndex(e => e.value === ProductInformationTabs.availability), 1);
        }
        if (!operatorAccessLevels.packages.lookup.editProductPropertyGroups) {
            tabs = tabs.filter(e => !e.value.startsWith('property-group-'));
        }
        if (!operatorAccessLevels.packages.bizInventory.adjustmentItems || !operatorAccessLevels.packages.lookup.editProductInventory) {
            tabs.splice(tabs.findIndex(e => e.value === ProductInformationTabs.inventoryAdjustmentRecord), 1);
        }
        if (!operatorAccessLevels.packages.bizInventory.adjustmentReports || !operatorAccessLevels.packages.lookup.editProductInventory) {
            tabs.splice(tabs.findIndex(e => e.value === ProductInformationTabs.inventoryAdjustmentReport), 1);
        }
        if (!operatorAccessLevels.packages.lookup.editProductTechnicalInformation) {
            tabs.splice(tabs.findIndex(e => e.value === ProductInformationTabs.technical), 1);
        }
        if (!operatorAccessLevels.packages.lookup.manageProductVariants) {
            tabs.splice(tabs.findIndex(e => e.value === ProductInformationTabs.variance), 1);
        }
        if (!operatorAccessLevels.packages.lookup.editProductImages) {
            tabs.splice(tabs.findIndex(e => e.value === ProductInformationTabs.images), 1);
        }
        if (!operatorAccessLevels.packages.lookup.editProductFeedbacks) {
            tabs.splice(tabs.findIndex(e => e.value === ProductInformationTabs.feedback), 1);
        }
        if (!operatorAccessLevels.packages.lookup.editProductTechnicalFiles) {
            tabs.splice(tabs.findIndex(e => e.value === ProductInformationTabs.technicalFiles), 1);
        }
        if (!operatorAccessLevels.packages.lookup.editProductNotes) {
            tabs.splice(tabs.findIndex(e => e.value === ProductInformationTabs.notes), 1);
        }
        if (!operatorAccessLevels.packages.lookup.editProductRelations) {
            tabs.splice(tabs.findIndex(e => e.value === ProductInformationTabs.relation), 1);
        }
        if (!operatorAccessLevels.packages.base.viewVendorProducts) {
            tabs.splice(tabs.findIndex(e => e.value === ProductInformationTabs.vendors), 1);
        }
        if (!operatorAccessLevels.packages.lookup.editProductCodes) {
            tabs.splice(tabs.findIndex(e => e.value === ProductInformationTabs.codes), 1);
        }
        if (!operatorAccessLevels.packages.dashboard.productInformationStats) {
            tabs.splice(tabs.findIndex(e => e.value === ProductInformationTabs.stats), 1);
        }
        if (!operatorAccessLevels.packages.lookup.editProductDimensions) {
            tabs.splice(tabs.findIndex(e => e.value === ProductInformationTabs.dimensions), 1);
        }
        return tabs;
    }, [operatorAccessLevels.packages.bizInventory.adjustmentItems, operatorAccessLevels.packages.bizInventory.adjustmentReports, operatorAccessLevels.packages.dashboard.productInformationStats, operatorAccessLevels.packages.lookup.editProductBasicInformation, operatorAccessLevels.packages.lookup.editProductCodes, operatorAccessLevels.packages.lookup.editProductDimensions, operatorAccessLevels.packages.lookup.editProductFeedbacks, operatorAccessLevels.packages.lookup.editProductImages, operatorAccessLevels.packages.lookup.editProductInventory, operatorAccessLevels.packages.lookup.editProductNotes, operatorAccessLevels.packages.lookup.editProductPricing, operatorAccessLevels.packages.lookup.editProductPropertyGroups, operatorAccessLevels.packages.lookup.editProductRelations, operatorAccessLevels.packages.lookup.editProductTechnicalFiles, operatorAccessLevels.packages.lookup.editProductTechnicalInformation, operatorAccessLevels.packages.base.viewVendorProducts, operatorAccessLevels.packages.lookup.manageProductVariants, propertyGroups]);

    const accessToNoSection = useMemo(() => tabs.length === 0, [tabs.length]);

    /**
     * Fetches the statistics of the given tags from the server.
     *
     * * if the response of the api is successful, sets the repeatCount of the tags, and suggestedTags of the product.
     * @param {any[]} tags
     * @param {any[]} suggestedTags
     * @return {Promise<void>}
     */
    const getProductTagsStatistics = useCallback(async (tags, suggestedTags) => {
        const ids = Array.from(new Set([
            ...(tags?.map(e => e.id) ?? []),
            ...(suggestedTags?.map(e => e.id) ?? [])
        ]).values())
        const forApi = {
            ids: ids,
        }
        const response = await Api.getTagsStatistics(forApi);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setProduct(prevState => ({
                ...prevState,
                tags: tags?.map(t => {
                    const found = response?.data?.find(e => e.id === t.id) ?? {};
                    return ({
                        ...t,
                        repeatCount: (found.numberOfConnectedPart ?? 0) + (found.numberOfConnectedBanner ?? 0)
                    })
                }) ?? [],
                suggestedTags: suggestedTags?.map(t => {
                    const found = response?.data?.find(e => e.id === t.id) ?? {};
                    return ({
                        ...t,
                        repeatCount: (found.numberOfConnectedPart ?? 0) + (found.numberOfConnectedBanner ?? 0)
                    })
                }) ?? [],
            }))
        }
    }, [isMounted])

    /**
     * Fetches the product's key information from the server.
     * @type {(function(string): Promise<void>)|*}
     */
    const getProductKeyInformation = useCallback(async (partNo) => {
        if (!partNo)
            return;
        const response = await Api.getProductKeyInformation(partNo);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setProduct(prevState => ({
                ...prevState,
                cost: response.data.currentCost,
                avgCost: response.data.averageCost,
                shortDescription: response.data.shortDescription,
                isVisibleForClient: response.data.isVisibleForClient,
                title: response.data.title ?? "",
                isEligibleForOnlineOrdering: response.data.isEligibleForOnlineOrdering !== false,
                deliveryMode: response.data.deliveryMode,
            }));
        }
    }, [isMounted])


    /**
     * Gets the full information of the product from the server and stores the results in the local state.
     */
    const getProductInformation = useCallback(async () => {
        setLoading(true);
        const response = await Api.getOperatorPartFullInfo(params.id);
        if (!isMounted()) return;
        if (response?.resultFlag) {
            const product = response?.data ?? {};
            setProduct(product);
            void getProductTagsStatistics(product?.tags ?? [], product?.suggestedTags ?? []);
            await getProductKeyInformation(product?.partNo);
            if (!isMounted()) return;
        }
        setLoading(false);
    }, [getProductKeyInformation, getProductTagsStatistics, isMounted, params.id])

    /**
     * With each change in params:
     * - checks if the tab param has a correct value, and if not, changes the tab value to basic.
     */
    useEffect(() => {
        if (previousId.current !== params?.id) {
            previousId.current = params.id;
            void getProductInformation();
        }
    }, [params.id, getProductInformation]);

    /**
     * With each change in the tab param:
     * - checks if the tab param has a correct value, and if not, changes the tab value to basic.
     */
    useEffect(() => {
        const tab = params?.tab;
        if (!tab || !tabs.map(e => e.value).includes(tab)) {
            if (tab?.startsWith('property-group-') || loadingPropertyGroups) {
                return;
            }
            return history.replace(routeFunctions.products.single(params.id, ProductInformationTabs.basic));
        }
    }, [params?.tab, loadingPropertyGroups, params.id, tabs, history])

    /**
     * Fetches the property groups of the products from the server.
     * @type {(function(*, *): Promise<void>)}
     */
    const getPropertyGroups = useCallback(async (industryId, cancelTokenSource) => {
        setLoadingPropertyGroups(true);
        const propertyGroups = await getPropertyGroupsForIndustry(industryId, cancelTokenSource.token);
        if (!isMounted())
            return;
        if (!!propertyGroups) {
            setPropertyGroups(propertyGroups ?? []);
        }
        if (!cancelTokenSource?.token?.reason) {
            setLoadingPropertyGroups(false);
        }
    }, [isMounted, getPropertyGroupsForIndustry])

    /**
     * As soon as the component is mounted:
     * - fetches the property groups of the product if the view is in edit mode.
     */
    useEffect(() => {
        if (!product?.industryId) {
            return setLoadingPropertyGroups(false);
        }
        const axiosCancelTokenSource = axios.CancelToken.source();
        void getPropertyGroups(product?.industryId, axiosCancelTokenSource);
        return () => axiosCancelTokenSource.cancel();
    }, [getPropertyGroups, product?.industryId])

    /**
     * Changes the tab value of the params to the new value to reflect the change.
     * @type {(function(*, *): void)}
     */
    const onTabChanged = useCallback((e, newValue) => {
        history.push(routeFunctions.products.single(params.id, newValue));
    }, [history, params.id])

    /**
     * Generate the path for the sub-routes of this view.
     * @type {(function(*): string)}
     */
    const generateRoute = useCallback((tab) => routeFunctions.products.single(':id', tab), []);


    /**
     * Opens the print dialog.
     * @type {(function(): void)}
     */
    const openPrintDialog = useCallback(async () => {
        const imagesResponse = await Api.getOperatorPartImages(params.id);
        const images = imagesResponse?.data ?? [];
        setBizPrintDialogState({
            open: true,
            pageName: BizPrintService.pages.productInformation.name,
            pageData: {
                title: product?.title ?? "",
                partNo: product?.partNo ?? product.partNumber,
                upc: product?.upc,
                cost: product?.cost,
                avgCost: product?.avgCost,
                mainCategory: product?.category?.title,
                image: (images?.find(e => e.isCover) ?? (images?.length ? images[0] : undefined))?.url,
                description: product?.description,
            },
        })
    }, [params.id, product?.avgCost, product?.category?.title, product?.cost, product?.description, product?.partNo, product?.partNumber, product?.title, product?.upc])

    const tabValue = useMemo(() => tabs.map(e => e.value).includes(tab) ? tab : false, [tab, tabs])

    /**
     * Sets the cache needed value of the product.
     * @type {(function(*): void)}
     */
    const setIsCacheNeeded = useCallback((value) => {
        if (!isMounted())
            return;
        setProduct(prevState => ({
            ...prevState,
            cachedNeeded: value,
        }))
    }, [isMounted])

    /**
     * Marks the product for cache needed.
     * @type {(function(): Promise<void>)|*}
     */
    const markForCacheNeeded = useCallback(async () => {
        setMarkingForCache(true);
        const response = await Api.markPartsForCache({
            partIds: [Number(params.id)],
            sectionNames: Object.values(MarkProductForCacheSectionNames),
        })
        if (!isMounted())
            return;
        setMarkingForCache(false);
        if (response?.resultFlag) {
            setIsCacheNeeded?.(true)
        }
    }, [isMounted, params.id, setIsCacheNeeded])

    /**
     * Opens the copy product dialog.
     * @type {(function(*))}
     */
    const openCopyProductDialog = useCallback((mode, close) => {
        close();
        setCopyProductDialogState({
            open: true,
            close: (refresh) => {
                setCopyProductDialogState(initialCopyProductDialogState)
                if (refresh) {
                    // refresh the window to reflect the changes.
                    window.location.reload();
                }
            },
            mode: mode,
            providedProduct: {
                id: product?.id,
                partNo: product?.partNo,
                industryId: product?.industryId,
                type: product?.type,
                category: product?.category,
            },
        })
    }, [product?.category, product?.id, product?.industryId, product?.partNo, product?.type])

    /**
     * Opens the edit part number dialog.
     * @type {(function(): void)|*}
     */
    const openEditPartNumberDialog = useCallback(() => {
        setEditPartNumberDialogState({
            open: true,
            partId: product?.id,
            partNo: product?.partNo,
            onSuccess: (newPartNo) => {
                setProduct(prevState => ({
                    ...prevState,
                    partNo: newPartNo,
                }))
            },
            close: (refresh) => {
                setEditPartNumberDialogState(initialEditPartNumberDialogState)
                if (refresh) {
                    getProductInformation().then();
                }
            },
        })
    }, [getProductInformation, product?.id, product?.partNo])

    /**
     * Sets the part number of the product.
     * @type {(function(*): void)|*}
     */
    const setPartUPC = useCallback((upc) => {
        setProduct(prevState => ({
            ...prevState,
            upc: upc,
        }));
    }, [])

    /**
     * Removes the current product and navigates back to the products list on success.
     */
    const removeProduct = useCallback(async () => {
        const partId = product?.id ?? Number(params.id);
        setRemoving(true);
        const response = await Api.removeProduct({partId});
        if (!isMounted())
            return;
        setRemoving(false);
        if (response?.resultFlag) {
            history.replace(routes.products.list);
        }
    }, [history, isMounted, params.id, product?.id])

    /**
     * Opens a confirmation dialog before removing the product.
     */
    const getRemoveConfirmation = useCallback(() => {
        window.partner?.openConfirmationDialog(
            <div className={'p-3'}>
                <Alert severity={'error'} variant={'filled'} sx={{mb: 2}}>
                    {t('remove.alert')}
                </Alert>
                <p className={'text-sm text-color-2 mb-0'}>
                    {t('remove.confirmation', {partNo: product?.partNo ?? ''})}
                </p>
            </div>,
            {
                maxWidth: 'sm'
            },
            (v) => v && removeProduct(),
            t('remove.proceed'),
            t('remove.cancel'),
        );
    }, [product?.partNo, removeProduct, t])

    return (
        <>
            <div className={'d-flex flex-wrap w-100 operator-product-information-view'}>
                <div className={'d-flex w-100 align-items-center justify-content-between'}>
                    <div className={'d-flex align-items-center'} style={{gap: 8}}>
                        {
                            product?.industryId &&
                            cloneElement(
                                UIUtils.getIndustryIcon(null, product?.industryId) ?? <div/>,
                                {
                                    style: {width: 40, height: 40},
                                    className: 'tertiary-main'
                                }
                            )
                        }
                        <ProductTitleText
                            loading={loading}
                            partNo={product?.partNo ?? product?.partNumber ?? "Product Information"}
                            title={product?.title}
                            showPartNoPopover={false}
                            showCopy={!!(product?.partNo ?? product?.partNumber)}
                        />
                        {
                            !!product && !loading &&
                            <>
                                <div className={classnames('checkmark-container')}>
                                    {
                                        !product.cachedNeeded
                                            ? <>
                                                <Tooltip
                                                    title={'This product\'s information is up to date when brought back in the search result'}>
                                                    <SaveIcon className={classnames(`save`, "mr-1")}/>
                                                </Tooltip>
                                                <Tooltip
                                                    title={'Request this product\'s info to be indexed again for search engines.'}>
                                                    <span>
                                                        <IconButton
                                                            disabled={loading || markingForCache}
                                                            className={"mx-1"}
                                                            color={'paper.secondary'}
                                                            onPressed={markForCacheNeeded}
                                                        >
                                                            {
                                                                markingForCache
                                                                    ? <CircularProgress size={15}/>
                                                                    : <CloudSyncOutlined className={'icon'}/>
                                                            }
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                            </>
                                            : <>
                                                {
                                                    !markingForCache &&
                                                    <Tooltip
                                                        title={'This product\'s values may not reflect their most updated state when appearing in the search result.'}
                                                    >
                                                        <WarningOutlined color={'warning'}/>
                                                    </Tooltip>
                                                }
                                            </>
                                    }
                                    {
                                        operatorAccessLevels.packages.lookup.editProductPartNo &&
                                        <Tooltip title={'Edit Part Number of this product'}>
                                            <Box component={'span'}>
                                                <IconButton
                                                    disabled={loading || markingForCache}
                                                    className={"mx-1"}
                                                    color={'paper.secondary'}
                                                    onPressed={openEditPartNumberDialog}
                                                >
                                                    <EditOutlined className={'icon'}/>
                                                </IconButton>
                                            </Box>
                                        </Tooltip>
                                    }
                                </div>
                            </>
                        }
                    </div>
                    <div className={'d-flex align-items-center justify-content-end'}>
                        {
                            operatorAccessLevels.packages.lookup.lookupProductInformation &&
                            !accessToNoSection &&
                            <Tooltip title={'View Product'}>
                                <Box component={'span'}>
                                    <IconButton
                                        disabled={loading}
                                        color={'paper.secondary'}
                                        component={Link}
                                        to={routeFunctions.lookup.single(product?.partNo)}
                                        target={'_blank'}
                                    >
                                        <Visibility/>
                                    </IconButton>
                                </Box>
                            </Tooltip>
                        }
                        {
                            operatorAccessLevels.packages.bizPrint.printProductInformation &&
                            <Tooltip title={'Print Product'}>
                                <Box component={'span'}>
                                    <IconButton
                                        disabled={loading}
                                        className={"mx-1 ml-2"}
                                        color={'paper.secondary'}
                                        onPressed={openPrintDialog}
                                    >
                                        <SvgIcon className={"icon normal"} component={Printer}/>
                                    </IconButton>
                                </Box>
                            </Tooltip>
                        }
                        {
                            operatorAccessLevels.packages.lookup.copyProduct &&
                            <Popover
                                elevation={2}
                                transformOrigin={{
                                    vertical: 'top',
                                    horizontal: 'right',
                                }}
                                child={(onEnter) =>
                                    <IconButton
                                        disabled={loading || removing}
                                        className={"mx-1 ml-2"}
                                        color={'paper.secondary'}
                                        onPressed={onEnter}
                                    >
                                        <SvgIcon className={"icon normal"} component={SettingsOutlined}/>
                                    </IconButton>
                                }
                                popoverChild={(_, close) => (
                                    <div className={'address-card-popover-actions'}>
                                        <div
                                            className={classnames('action-item d-flex align-items-center')}
                                            onClick={() => openCopyProductDialog(CopyProductDialogModes.copyFrom, close)}
                                        >
                                            <CopyAllRounded color={'secondary'} className={'mr-2'}/>
                                            <p>Copy From</p>
                                        </div>
                                        <div
                                            className={classnames('action-item d-flex align-items-center')}
                                            onClick={() => openCopyProductDialog(CopyProductDialogModes.makeCopy, close)}
                                        >
                                            <FiberNewRounded color={'secondary'} className={'mr-2'}/>
                                            <p>Make a Copy</p>
                                        </div>
                                    </div>
                                )}
                            />
                        }
                        {
                            operatorAccessLevels.packages.lookup.removeProduct &&
                            <Tooltip title={t('remove.tooltip')}>
                                <Box component={'span'}>
                                    <IconButton
                                        disabled={loading || removing}
                                        className={"mx-1 ml-2"}
                                        color={'paper.error'}
                                        onPressed={getRemoveConfirmation}
                                    >
                                        <DeleteOutlined/>
                                    </IconButton>
                                </Box>
                            </Tooltip>
                        }
                    </div>
                </div>
                {
                    accessToNoSection &&
                    <>
                        <div className={'w-100 mt-3'}>
                            <div className={'text-md font-weight-bold black mb-3'}>
                                You do not have access to any sections of this product. please contact your
                                administrator.
                                {operatorAccessLevels.packages.lookup.lookupProductInformation ? 'You can view the product information by clicking the button below.' : ''}
                            </div>
                            {
                                operatorAccessLevels.packages.lookup.lookupProductInformation &&
                                <Button
                                    variant={'outlined'}
                                    color={'primary'}
                                    disabled={loading}
                                    component={Link}
                                    to={routeFunctions.lookup.single(product?.partNo)}
                                >
                                    {
                                        loading
                                            ? "loading..."
                                            : "View Product"
                                    }
                                </Button>
                            }
                        </div>
                    </>
                }
                {
                    !accessToNoSection &&
                    <>
                        <div className={'w-100 mt-3'}>
                            <Tabs value={tabValue} onChange={onTabChanged}>
                                {
                                    tabs?.map((tab) => (
                                        <Tab
                                            key={tab.value}
                                            value={tab.value}
                                            label={tab.name}
                                        />
                                    ))
                                }
                            </Tabs>
                        </div>
                        <Col xs={12} className={'my-2'}/>
                        <Switch>
                            {
                                operatorAccessLevels.packages.lookup.editProductBasicInformation &&
                                <Route path={generateRoute(ProductInformationTabs.basic)} exact>
                                    <ProductInformationBasicInformationView
                                        product={product}
                                        setProduct={setProduct}
                                        loading={loading}
                                        setIsCacheNeeded={setIsCacheNeeded}
                                    />
                                </Route>
                            }
                            {
                                operatorAccessLevels.packages.lookup.editProductPricing &&
                                <Route path={generateRoute(ProductInformationTabs.price)} exact>
                                    <ProductInformationPricesView
                                        product={product}
                                        loading={loading}
                                        setIsCacheNeeded={setIsCacheNeeded}
                                    />
                                </Route>
                            }
                            {
                                operatorAccessLevels.packages.lookup.editProductInventory &&
                                <Route path={generateRoute(ProductInformationTabs.availability)} exact>
                                    <ProductInformationAvailabilityView
                                        loading={loading}
                                        partNo={product?.partNo ?? product?.partNumber}
                                        partId={product?.id ?? Number(params.id)}
                                        cost={product?.cost}
                                        isSerializable={product?.isSerializable}
                                        setIsCacheNeeded={setIsCacheNeeded}
                                    />
                                </Route>
                            }
                            {
                                operatorAccessLevels.packages.lookup.editProductInventory &&
                                operatorAccessLevels.packages.bizInventory.adjustmentReports &&
                                <Route path={generateRoute(ProductInformationTabs.inventoryAdjustmentReport)}
                                       exact>
                                    <InventoryAdjustmentReportView
                                        loading={loading}
                                        product={{
                                            categoryId: product?.category?.parentId ?? product?.category?.id,
                                            partNo: product?.partNo,
                                            id: product?.id
                                        }}
                                        setIsCacheNeeded={setIsCacheNeeded}
                                    />
                                </Route>
                            }
                            {
                                operatorAccessLevels.packages.lookup.editProductInventory &&
                                operatorAccessLevels.packages.bizInventory.adjustmentItems &&
                                <Route path={generateRoute(ProductInformationTabs.inventoryAdjustmentRecord)}
                                       exact>
                                    {
                                        loading &&
                                        <>
                                            <div className={'inventory-adjustment-record-view'}>
                                                <LoadingLottieAnimation
                                                    width={'500px'}
                                                    height={'50vh'}
                                                />
                                            </div>
                                        </>
                                    }
                                    {
                                        !loading &&
                                        <InventoryAdjustmentRecordView
                                            loading={loading}
                                            product={{
                                                partNo: product.partNo,
                                                id: product.id
                                            }}
                                            setIsCacheNeeded={setIsCacheNeeded}
                                        />
                                    }
                                </Route>
                            }
                            {
                                operatorAccessLevels.packages.lookup.editProductPropertyGroups &&
                                propertyGroups.map(e => (
                                    <Route key={e.id} path={generateRoute(`property-group-${e.id}`)} exact>
                                        <ProductInformationPropertyGroupView
                                            loading={loading}
                                            groupId={e.id}
                                            setIsCacheNeeded={setIsCacheNeeded}
                                        />
                                    </Route>
                                ))
                            }
                            {
                                operatorAccessLevels.packages.lookup.editProductTechnicalInformation &&
                                <Route path={generateRoute(ProductInformationTabs.technical)} exact>
                                    <ProductTechnicalPropertiesSection
                                        loading={loading}
                                        product={product}
                                        setIsCacheNeeded={setIsCacheNeeded}
                                    />
                                </Route>
                            }
                            {
                                operatorAccessLevels.packages.lookup.manageProductVariants &&
                                <Route path={generateRoute(ProductInformationTabs.variance)} exact>
                                    <ProductVarianceSection
                                        loading={loading}
                                        product={product}
                                        setIsCacheNeeded={setIsCacheNeeded}
                                    />
                                </Route>
                            }
                            {
                                operatorAccessLevels.packages.lookup.editProductImages &&
                                <Route path={generateRoute(ProductInformationTabs.images)} exact>
                                    <ProductInformationImagesView
                                        loading={loading}
                                        setIsCacheNeeded={setIsCacheNeeded}
                                    />
                                </Route>
                            }
                            {
                                operatorAccessLevels.packages.lookup.editProductFeedbacks &&
                                <Route path={generateRoute(ProductInformationTabs.feedback)} exact>
                                    <ProductInformationFeedbacksView
                                        loading={loading}
                                        setIsCacheNeeded={setIsCacheNeeded}
                                    />
                                </Route>
                            }
                            {
                                operatorAccessLevels.packages.lookup.editProductTechnicalFiles &&
                                <Route path={generateRoute(ProductInformationTabs.technicalFiles)} exact>
                                    <ProductInformationTechnicalFilesView
                                        loading={loading}
                                        product={product}
                                        setIsCacheNeeded={setIsCacheNeeded}
                                    />
                                </Route>
                            }
                            {
                                operatorAccessLevels.packages.lookup.editProductNotes &&
                                <Route path={generateRoute(ProductInformationTabs.notes)} exact>
                                    <ProductInformationNotesView
                                        loading={loading}
                                        setIsCacheNeeded={setIsCacheNeeded}
                                    />
                                </Route>
                            }
                            {
                                operatorAccessLevels.packages.lookup.editProductRelations &&
                                <Route path={generateRoute(ProductInformationTabs.relation)} exact>
                                    <ProductInformationRelationsView
                                        loading={loading}
                                        setIsCacheNeeded={setIsCacheNeeded}
                                    />
                                </Route>
                            }
                            {
                                operatorAccessLevels.packages.lookup.editProductCodes &&
                                <Route path={generateRoute(ProductInformationTabs.codes)} exact>
                                    <ProductInformationCodesView
                                        loading={loading}
                                        productId={Number(params.id)}
                                        setIsCacheNeeded={setIsCacheNeeded}
                                        setPartUPC={setPartUPC}
                                    />
                                </Route>
                            }
                            {
                                operatorAccessLevels.packages.dashboard.productInformationStats &&
                                <Route path={generateRoute(ProductInformationTabs.stats)} exact>
                                    <ProductInformationStatsView
                                        loading={loading}
                                        partNo={product?.partNo ?? product?.partNumber}
                                    />
                                </Route>
                            }
                            {
                                operatorAccessLevels.packages.lookup.editProductDimensions &&
                                <Route path={generateRoute(ProductInformationTabs.dimensions)} exact>
                                    <ProductInformationDimensionsView
                                        loading={loading}
                                        partNo={product?.partNo ?? product?.partNumber}
                                        partId={product?.id ?? Number(params.id)}
                                    />
                                </Route>
                            }
                            {
                                operatorAccessLevels.packages.base.viewVendorProducts &&
                                <Route path={generateRoute(ProductInformationTabs.vendors)} exact>
                                    <ProductInformationVendorsView
                                        loading={loading}
                                        partNo={product?.partNo ?? product?.partNumber}
                                        partId={product?.id ?? Number(params.id)}
                                    />
                                </Route>
                            }
                        </Switch>
                        {
                            (loadingPropertyGroups || loading) && tab?.startsWith('property-group-') &&
                            <>
                                <div className={'d-flex align-items-center justify-content-center'}>
                                    <LoadingLottieAnimation
                                        width={'80%'}
                                    />
                                </div>
                            </>
                        }
                    </>
                }
                {
                    operatorAccessLevels.packages.bizPrint.printProductInformation &&
                    <BizPrintTemplatesDialog
                        open={bizPrintDialogState.open}
                        close={() => setBizPrintDialogState(initialBizPrintDialogState)}
                        pageName={bizPrintDialogState.pageName}
                        pageData={bizPrintDialogState.pageData}
                    />
                }
                {
                    operatorAccessLevels.packages.lookup.copyProduct &&
                    <CopyProductDialog
                        open={copyProductDialogState.open}
                        close={copyProductDialogState.close}
                        mode={copyProductDialogState.mode}
                        providedProduct={copyProductDialogState.providedProduct}
                    />
                }
                {
                    operatorAccessLevels.packages.lookup.editProductPartNo &&
                    <EditProductPartNumberDialog
                        open={editPartNumberDialogState.open}
                        partId={editPartNumberDialogState.partId}
                        partNo={editPartNumberDialogState.partNo}
                        onSuccess={editPartNumberDialogState.onSuccess}
                        close={editPartNumberDialogState.close}
                    />
                }
            </div>
        </>
    )
}

export const ProductTitleText = ({
                                     loading,
                                     partNo,
                                     title,
                                     mainTextVariant = 'body1',
                                     secondaryTextVariant = 'caption',
                                     mainTextColor = 'text.primary',
                                     secondaryTextColor,
                                     partNoClassName,
                                     titleClassName,
                                     className,
                                     maxLines,
                                     tooltipPlacement = 'top',
                                     showPartNoPopover = true,
                                     showCopy = false,
                                     partNoPopoverProps,
                                     PartNoWrapper,
                                     TitleWrapper
                                 }) => {
    const hasTitle = !!title;
    const primaryText = hasTitle ? title ?? "" : partNo ?? "";

    const clampSx = maxLines ? {
        display: '-webkit-box',
        WebkitLineClamp: maxLines,
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
        overflowWrap: 'anywhere',
        minWidth: 0,
        width: '100%',
    } : {};

    const wrapWithTooltip = (content, tooltipText) => {
        if (!maxLines || !tooltipText) {
            return content;
        }

        return (
            <Tooltip title={tooltipText} placement={tooltipPlacement} arrow>
                <Box component={'span'} sx={{display: 'block', minWidth: 0, width: '100%'}}>
                    {content}
                </Box>
            </Tooltip>
        );
    };

    const [renderedPartNoPopover, renderedPartNoPopoverBelowTitle] = React.useMemo(() => {
        const hasPartNoWrapper = !!PartNoWrapper;
        const PartNoPopover = showPartNoPopover ? ProductInformationPopover : ({key, children}) => <React.Fragment key={key}>{children}</React.Fragment>;
        let renderedPartNoPopover = (
            <PartNoPopover {...partNoPopoverProps} partNo={!showPartNoPopover ? "" : partNo}>
                {partNo ?? ""}
            </PartNoPopover>
        )
        const partNoColor = secondaryTextColor ?? (!showPartNoPopover ? 'text.secondary' : 'inherit');
        let renderedPartNoPopoverBelowTitle = (
            <PartNoPopover {...partNoPopoverProps} partNo={!showPartNoPopover ? "" : partNo}>
                <Typography component={'span'} variant={secondaryTextVariant || 'caption'} color={partNoColor} fontWeight={600}
                            className={`${partNoClassName ?? ""} ${className ?? ""} second-line`}
                            sx={{
                                ...clampSx,
                                ...(maxLines ? {WebkitLineClamp: 1} : {}),
                            }}>
                    {partNo ?? ""}
                </Typography>
            </PartNoPopover>
        )
        if (hasPartNoWrapper) {
            renderedPartNoPopover = (
                <PartNoWrapper partNo={partNo ?? ""}>
                    {renderedPartNoPopover}
                </PartNoWrapper>
            )
            renderedPartNoPopoverBelowTitle = (
                <PartNoWrapper partNo={partNo ?? ""}>
                    {renderedPartNoPopoverBelowTitle}
                </PartNoWrapper>
            )
        }
        if (showCopy && partNo) {
            renderedPartNoPopover = (
                <Box component={'span'} sx={{display: 'inline-flex', alignItems: 'center', minWidth: 0}}>
                    {renderedPartNoPopover}
                    <CopyIconButton value={partNo}/>
                </Box>
            )
            renderedPartNoPopoverBelowTitle = (
                <Box component={'span'} sx={{display: 'inline-flex', alignItems: 'center', minWidth: 0}}>
                    {renderedPartNoPopoverBelowTitle}
                    <CopyIconButton value={partNo}/>
                </Box>
            )
        }
        return [
            renderedPartNoPopover,
            renderedPartNoPopoverBelowTitle,
        ]
    }, [PartNoWrapper, showPartNoPopover, showCopy, partNoPopoverProps, partNo, secondaryTextColor, secondaryTextVariant, partNoClassName, className, clampSx, maxLines])

    const [renderedTitle] = React.useMemo(() => {
        let renderedTitle = title ?? "";
        if (TitleWrapper) {
            renderedTitle = (
                <TitleWrapper title={title ?? ""}>
                    {renderedTitle}
                </TitleWrapper>
            )
        }
        return [renderedTitle]
    }, [TitleWrapper, title])


    if (loading) {
        return (
            <Skeleton sx={{transform: 'none'}} width={100} height={35}/>
        );
    }

    const primaryTypography = (
        <Typography
            component={'span'} variant={mainTextVariant || 'body1'} color={mainTextColor || 'text.primary'} fontWeight={700}
            className={`${!hasTitle ? partNoClassName : titleClassName} ${className ?? ""}`}
            sx={clampSx}
        >
            {hasTitle ? renderedTitle : renderedPartNoPopover}
        </Typography>
    );

    return (
        <Box component={'span'} sx={{display: 'flex', flexDirection: 'column', minWidth: 0, width: '100%'}}>
            {
                wrapWithTooltip(primaryTypography, primaryText)
            }
            {hasTitle && wrapWithTooltip(renderedPartNoPopoverBelowTitle, partNo)}
        </Box>
    );
}

export default ProductInformationView;
